import { useMutation } from '@tanstack/react-query';
import { showToast } from '@tix-ticketing/ui-components';
import { useIntl } from 'react-intl';
import { postApiOrdersByEncryptedOrderIdSpecialOffersBySpecialOfferId } from 'api/generated/sdk.gen';
import { useAppState } from 'lib/app-state';

export const useClaimSpecialOffer = (options?: {
  onSuccess?: (
    data: any,
    variables: { specialOfferId: number; password?: string | null }
  ) => void;
  onError?: (data: any) => void;
}) => {
  const intl = useIntl();
  const appState = useAppState();

  const mutation = useMutation({
    mutationFn: async ({
      specialOfferId,
      password,
    }: {
      specialOfferId: number;
      password?: string | null;
    }) => {
      // Check if already applied to order
      const currentSpecialOfferAndPass =
        appState.eventData?.order?.specialOfferPassword;
      if (currentSpecialOfferAndPass?.specialOfferId === specialOfferId) {
        return { skipped: true, specialOfferId };
      }

      return postApiOrdersByEncryptedOrderIdSpecialOffersBySpecialOfferId({
        path: {
          encryptedOrderId: appState.eventData?.order?.encryptedId ?? '0',
          specialOfferId: specialOfferId,
        },
        body: {
          password: password ?? null,
        },
      });
    },
    onSuccess: (data: any, variables) => {
      if (data.skipped) {
        showToast({
          title: intl.formatMessage({
            id: 'common.success',
            defaultMessage: 'Success',
          }),
          message: intl.formatMessage({
            id: 'offer.claimed',
            defaultMessage: 'Offer Claimed',
          }),
          severity: 'success',
        });
        if (options?.onSuccess) {
          options.onSuccess(data, variables);
        }
        return;
      }

      if (data.status !== 200) {
        showToast({
          title:
            data.response.data.title ??
            intl.formatMessage({
              id: 'offer.claimWarning.title',
              defaultMessage: 'Could not claim offer',
            }),
          message:
            data.response.data.detail ||
            intl.formatMessage({
              id: 'offer.claimWarning.message',
              defaultMessage:
                'There was an issue claiming this offer. Please try again later.',
            }),
          severity: 'error',
        });
        if (options?.onError) {
          options.onError(data);
        }
      } else {
        showToast({
          title: intl.formatMessage({
            id: 'common.success',
            defaultMessage: 'Success',
          }),
          message: intl.formatMessage({
            id: 'offer.claimed',
            defaultMessage: 'Offer Claimed',
          }),
          severity: 'success',
        });

        appState.setEventData({
          event: appState.eventData.event,
          order: data.data.order,
        });

        if (options?.onSuccess) {
          options.onSuccess(data, variables);
        }
      }
    },
    onError: (e) => {
      showToast({
        title: intl.formatMessage({
          id: 'offer.claimWarning.title',
          defaultMessage: 'Could not claim offer',
        }),
        message: intl.formatMessage({
          id: 'offer.claimWarning.message',
          defaultMessage:
            'There was an issue claiming this offer. Please try again later.',
        }),
        severity: 'error',
      });
      if (options?.onError) {
        options.onError(e);
      }
    },
  });

  return mutation;
};
